import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Autocomplete,
  Box,
  createFilterOptions,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import GenericDialog from "@cmp/genericDialog"
import { ProgramClient } from "@clients/programClient"
import { ProgramClassClient } from "@clients/programClassClient"
import { TrackClient } from "@clients/trackClient"
import {
  ProgramClassDto,
  ProgramDto,
  TrackDto,
} from "@masterschool/course-builder-api"
import { domainToDisplayName } from "@utils/domainUtils"
import { legacyProgramDisplayName } from "../../programs/program/ui/program+ui"
import { formatProgramLabel } from "./programLabel"
import StudentLanguageSelector from "../../programs/program/students/studentLanguageSelector"
import {
  fetchProgramPreviews,
  formatPreviewRows,
  NO_ACTIVE_STUDENTS_WARNING,
  ProgramPreview,
} from "./programPreview"

const ALL_SCHOOLS = "all"

// How many of the currently visible options get their preview fetched in one
// batched call — roughly what fits in the open listbox.
const PREVIEW_BATCH_SIZE = 15

// Same default matching the Autocomplete itself uses, so the batch we preview
// is exactly the top of the list the user is looking at.
const filterProgramOptions = createFilterOptions<ProgramDto>()

// The Autocomplete otherwise mounts one row per match — hundreds of programs —
// and re-renders them on every keystroke, blocking the main thread. Cap what
// renders to the top matches (the user narrows by typing); this is the bounded
// list the optimized calendar picker uses.
const limitedProgramFilter = createFilterOptions<ProgramDto>({ limit: 100 })

// Programs campus knows to be empty (0 active students, or no campus row at
// all) sink below the rest — live cohorts surface first, litter last. Order
// within each half is untouched (still most recent first). Programs whose
// stats haven't arrived yet count as live so nothing jumps around while
// previews stream in.
function sinkEmptyPrograms(
  programs: ProgramDto[],
  previews: Map<string, ProgramPreview | null>,
): ProgramDto[] {
  const live: ProgramDto[] = []
  const empty: ProgramDto[] = []
  for (const program of programs) {
    const preview = previews.get(program.id)
    const isKnownEmpty =
      preview === null ||
      (preview !== undefined && preview.activeStudents === 0)
    if (isKnownEmpty) {
      empty.push(program)
    } else {
      live.push(program)
    }
  }
  return [...live, ...empty]
}

// The card sits in a fixed-height reserved slot in the dialog (see the picker
// below) so the dialog never grows when a highlight mounts it — the height
// here is the slot's, and every card variant (loading, warning, full stats)
// fits inside it. Identity and detail are clamped to one line each so a long
// program/track name can't wrap and change the card's height either.
const PREVIEW_CARD_HEIGHT = 208

// The mini program widget the operator confirms against: the program's
// identity on top, its campus footprint as rows below. undefined preview =
// still loading (or fetch failed and will be retried), null = no campus row
// for this program. The warning states are the card's loudest line — they are
// what stops an operator from adding a student to a dead cohort.
function ProgramPreviewCard(props: {
  identity: string
  detail: string
  preview: ProgramPreview | null | undefined
  // When true the card fills its parent's height (the reserved picker slot)
  // instead of sizing to its content, so a shorter card can't shrink the slot.
  fillHeight?: boolean
}) {
  const { identity, detail, preview, fillHeight } = props
  const isWarning =
    preview === null || (preview != null && preview.activeStudents === 0)
  return (
    <Stack
      gap="2px"
      sx={{
        border: "1px solid",
        borderColor: isWarning ? "warning.main" : "divider",
        borderRadius: "8px",
        p: "10px 12px",
        height: fillHeight ? "100%" : undefined,
        overflow: "hidden",
      }}
    >
      <Typography variant="body2" fontWeight={500} noWrap title={identity}>
        {identity}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        title={detail}
      >
        {detail}
      </Typography>
      {preview === undefined && (
        <Typography variant="caption" color="text.secondary">
          Loading preview…
        </Typography>
      )}
      {preview === null && (
        <Typography variant="caption" color="warning.main" fontWeight={600}>
          No campus record for this program, possibly a test or stale duplicate
        </Typography>
      )}
      {preview != null && (
        <>
          {preview.activeStudents === 0 && (
            <Typography variant="caption" color="warning.main" fontWeight={600}>
              {NO_ACTIVE_STUDENTS_WARNING}
            </Typography>
          )}
          {formatPreviewRows(preview).map((row) => (
            <Typography key={row} variant="caption" color="text.secondary">
              {row}
            </Typography>
          ))}
        </>
      )}
    </Stack>
  )
}

// "Add to a program" picker: optionally narrow by school, choose a program
// (most recent first) and a class within it, then add the student additively —
// their other programs are untouched.
export function AddProgramDialog(props: {
  open: boolean
  studentId: string
  onClose: () => void
  onAdded: () => void
}) {
  const { open, studentId, onClose, onAdded } = props
  const [programs, setPrograms] = useState<ProgramDto[]>([])
  const [tracks, setTracks] = useState<TrackDto[]>([])
  const [schoolFilter, setSchoolFilter] = useState<string>(ALL_SCHOOLS)
  const [selectedProgram, setSelectedProgram] = useState<ProgramDto | null>(
    null,
  )
  const [classes, setClasses] = useState<ProgramClassDto[]>([])
  const [selectedClassId, setSelectedClassId] = useState<string>("")
  const [language, setLanguage] = useState<string | undefined>(undefined)
  const [isAdding, setIsAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Program previews (campus footprint per program id), cached for the
  // dialog's lifetime. null = campus has no row for that id. The version
  // counter only exists to re-render when a batch lands.
  const previewsRef = useRef(new Map<string, ProgramPreview | null>())
  const pendingPreviewIdsRef = useRef(new Set<string>())
  const [, setPreviewsVersion] = useState(0)
  const [highlightedProgram, setHighlightedProgram] =
    useState<ProgramDto | null>(null)
  const [programInput, setProgramInput] = useState("")
  // The picker being open is what reserves the preview card's height: the slot
  // is rendered the moment the list opens (empty until a highlight lands), so
  // the card mounting never grows the dialog.
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }
    // Fresh stats each time the dialog opens — a student may have just been
    // added or removed elsewhere.
    previewsRef.current.clear()
    pendingPreviewIdsRef.current.clear()
    ProgramClient.listPrograms()
      .then(setPrograms)
      .catch(() => setError("Couldn't load programs."))
    // Tracks carry the program's real name (its syllabus). Losing them only
    // degrades the label back to school + date, so failures stay silent.
    TrackClient.list()
      .then(setTracks)
      .catch(() => {})
  }, [open])

  useEffect(() => {
    setSelectedClassId("")
    setClasses([])
    if (!selectedProgram) {
      return
    }
    ProgramClient.listClasses(selectedProgram.id)
      .then(setClasses)
      .catch(() => setError("Couldn't load the program's classes."))
  }, [selectedProgram])

  // Schools that actually have programs, sorted by display name.
  const schools = useMemo(
    () =>
      Array.from(new Set(programs.map((program) => program.domain))).sort(
        (a, b) => domainToDisplayName(a).localeCompare(domainToDisplayName(b)),
      ),
    [programs],
  )

  // Programs in the chosen school, most recent first.
  const visiblePrograms = useMemo(
    () =>
      programs
        .filter(
          (program) =>
            schoolFilter === ALL_SCHOOLS || program.domain === schoolFilter,
        )
        .sort(
          (a, b) =>
            new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
        ),
    [programs, schoolFilter],
  )

  // The option order is a SNAPSHOT: recomputed when the base list changes and
  // when the popup opens, but never while the user is typing or moving the
  // highlight — previews landing mid-keystroke must not reshuffle the list
  // under the cursor. The next popup open folds the newest stats in.
  const [orderedPrograms, setOrderedPrograms] = useState<ProgramDto[]>([])
  useEffect(() => {
    setOrderedPrograms(sinkEmptyPrograms(visiblePrograms, previewsRef.current))
  }, [visiblePrograms])

  // The program's NAME is its track (same naming the calendar sidebar uses) —
  // school + date alone collide across the several tracks starting each term.
  // The label carries name, date and track so typing any of them finds it.
  const trackNameByTrackId = useMemo(() => {
    const names = new Map<string, string>()
    tracks.forEach((track) => {
      const name = track.externalName || track.internalName
      if (name) {
        names.set(track.id, name)
      }
    })
    return names
  }, [tracks])

  const programOptionLabel = useCallback(
    (program: ProgramDto) => {
      const trackName = program.syllabusId
        ? trackNameByTrackId.get(program.syllabusId)
        : undefined
      return [legacyProgramDisplayName(program), trackName]
        .filter(Boolean)
        .join(" · ")
    },
    [trackNameByTrackId],
  )

  const requestPreviews = useCallback((programIds: string[]) => {
    programIds.forEach((id) => pendingPreviewIdsRef.current.add(id))
    fetchProgramPreviews(programIds)
      .then((previews) => {
        // An id the response omits has no campus row — cache that as null so
        // we don't refetch it on every highlight.
        programIds.forEach((id) => {
          previewsRef.current.set(id, previews[id] ?? null)
        })
        setPreviewsVersion((version) => version + 1)
      })
      .catch(() => {
        // Previews are best-effort decoration; leaving the ids uncached lets
        // a later filter change or highlight retry them.
      })
      .finally(() => {
        programIds.forEach((id) => pendingPreviewIdsRef.current.delete(id))
      })
  }, [])

  // The top of the list as the user currently sees it (same filter the
  // Autocomplete applies). Whenever it changes, batch-fetch the missing
  // previews for those options in one call.
  const topVisibleIds = useMemo(() => {
    const filtered = filterProgramOptions(orderedPrograms, {
      inputValue: programInput,
      getOptionLabel: programOptionLabel,
    })
    return filtered.slice(0, PREVIEW_BATCH_SIZE).map((program) => program.id)
  }, [orderedPrograms, programInput, programOptionLabel])

  useEffect(() => {
    if (!open || topVisibleIds.length === 0) {
      return
    }
    // Small debounce so fast typing settles into one batch per pause instead
    // of one per keystroke.
    const timer = setTimeout(() => {
      const missing = topVisibleIds.filter(
        (id) =>
          !previewsRef.current.has(id) && !pendingPreviewIdsRef.current.has(id),
      )
      if (missing.length > 0) {
        requestPreviews(missing)
      }
    }, 250)
    return () => clearTimeout(timer)
  }, [open, topVisibleIds, requestPreviews])

  const previewForProgram = (program: ProgramDto | null) =>
    program ? previewsRef.current.get(program.id) : undefined

  // The card under the picker follows the keyboard highlight while the list
  // is open, and falls back to the chosen program once it's closed. When it
  // would show the chosen program, it yields to the confirm card below the
  // class select — one card per program, never the same card twice.
  const previewTarget = highlightedProgram ?? selectedProgram

  const reset = () => {
    setSchoolFilter(ALL_SCHOOLS)
    setSelectedProgram(null)
    setHighlightedProgram(null)
    setProgramInput("")
    setPickerOpen(false)
    setClasses([])
    setSelectedClassId("")
    setLanguage(undefined)
    setError(null)
  }

  const handleClose = () => {
    if (isAdding) {
      return
    }
    reset()
    onClose()
  }

  const onSchoolChange = (school: string) => {
    setSchoolFilter(school)
    setSelectedProgram(null)
    setHighlightedProgram(null)
  }

  const onAdd = () => {
    if (!selectedClassId || !language) {
      return
    }
    setIsAdding(true)
    setError(null)
    ProgramClassClient.addStudentToProgram({
      studentId,
      offeringClientId: selectedClassId,
      // The program the class belongs to. Adding a program must also give the
      // student that program's course classes there and then — the LMS resolves
      // which ones, and it needs the program to do it.
      programId: selectedProgram?.id,
      language,
    })
      .then(() => {
        reset()
        onAdded()
        onClose()
      })
      .catch(() => setError("Couldn't add the student. Please try again."))
      .finally(() => setIsAdding(false))
  }

  return (
    <GenericDialog
      open={open}
      onClose={handleClose}
      size="xs"
      title="Add to a program"
      content={
        <Stack gap="16px" pt="8px">
          {/* The closed select ellipsizes long school names in its input —
              the tooltip is the recourse to the full text. */}
          <Tooltip
            title={
              schoolFilter === ALL_SCHOOLS
                ? ""
                : domainToDisplayName(schoolFilter)
            }
          >
            <TextField
              select
              label="School"
              value={schoolFilter}
              onChange={(e) => onSchoolChange(e.target.value)}
            >
              <MenuItem value={ALL_SCHOOLS}>All schools</MenuItem>
              {schools.map((school) => (
                <MenuItem key={school} value={school}>
                  {domainToDisplayName(school)}
                </MenuItem>
              ))}
            </TextField>
          </Tooltip>
          <Autocomplete
            options={orderedPrograms}
            filterOptions={limitedProgramFilter}
            value={selectedProgram}
            autoHighlight
            onChange={(_, value) => {
              setSelectedProgram(value)
              setLanguage(value?.language)
              if (
                value &&
                !previewsRef.current.has(value.id) &&
                !pendingPreviewIdsRef.current.has(value.id)
              ) {
                requestPreviews([value.id])
              }
            }}
            onOpen={() => {
              setPickerOpen(true)
              // Fold the latest stats into the order exactly once per open —
              // never mid-interaction.
              setOrderedPrograms(
                sinkEmptyPrograms(visiblePrograms, previewsRef.current),
              )
            }}
            onClose={() => {
              setPickerOpen(false)
              setHighlightedProgram(null)
            }}
            onInputChange={(_, value) => setProgramInput(value)}
            onHighlightChange={(_, option) => {
              setHighlightedProgram(option)
              if (
                option &&
                !previewsRef.current.has(option.id) &&
                !pendingPreviewIdsRef.current.has(option.id)
              ) {
                requestPreviews([option.id])
              }
            }}
            getOptionLabel={programOptionLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(optionProps, program) => (
              // Long program/track names ellipsize in the row — a native title
              // carries both full lines. A per-row MUI Tooltip mounts a Popper
              // for every option and is what stalled the picker.
              <li
                {...optionProps}
                key={program.id}
                title={`${programOptionLabel(program)}\n${formatProgramLabel(
                  program,
                )}`}
              >
                <Stack sx={{ minWidth: 0, width: "100%" }}>
                  <Typography variant="body2" noWrap>
                    {programOptionLabel(program)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {formatProgramLabel(program)}
                  </Typography>
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <Tooltip
                title={
                  selectedProgram ? programOptionLabel(selectedProgram) : ""
                }
              >
                <TextField {...params} label="Program" />
              </Tooltip>
            )}
          />
          {/* Reserved slot: while the picker is open (or a highlighted program
              differs from the chosen one) the card lives in a fixed-height box,
              so a highlight landing — or moving between a short and a tall card
              — never resizes the dialog. */}
          {(pickerOpen ||
            (previewTarget && previewTarget.id !== selectedProgram?.id)) && (
            <Box sx={{ height: PREVIEW_CARD_HEIGHT, flexShrink: 0 }}>
              {previewTarget && previewTarget.id !== selectedProgram?.id && (
                <ProgramPreviewCard
                  identity={programOptionLabel(previewTarget)}
                  detail={formatProgramLabel(previewTarget)}
                  preview={previewForProgram(previewTarget)}
                  fillHeight
                />
              )}
            </Box>
          )}
          <Tooltip
            title={
              classes.find(
                (programClass) => programClass.id === selectedClassId,
              )?.name ?? ""
            }
          >
            <TextField
              select
              label="Class"
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
              disabled={!selectedProgram || classes.length === 0}
            >
              {classes.map((programClass) => (
                <MenuItem key={programClass.id} value={programClass.id}>
                  {programClass.name}
                </MenuItem>
              ))}
            </TextField>
          </Tooltip>
          <StudentLanguageSelector
            language={language}
            onLanguageChange={setLanguage}
          />
          {selectedProgram && (
            <Stack gap="4px">
              <Typography variant="caption" color="text.secondary">
                Adding to:
              </Typography>
              {/* The same card as the picker preview, so the operator
                  confirms against the full details, not a one-line echo. */}
              <ProgramPreviewCard
                identity={programOptionLabel(selectedProgram)}
                detail={formatProgramLabel(selectedProgram)}
                preview={previewForProgram(selectedProgram)}
              />
            </Stack>
          )}
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      }
      buttons={[
        {
          type: "secondary",
          text: "Cancel",
          onClick: handleClose,
          disabled: isAdding,
        },
        {
          type: "primary",
          text: "Add",
          onClick: onAdd,
          loading: isAdding,
          disabled: !selectedClassId || !language,
        },
      ]}
    />
  )
}
